import { useState } from 'react';
import { Mail, MessageSquare, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { Select } from '../components/ui/Select';
import { Card, CardContent } from '../components/ui/Card';
import { createInquiry } from '../lib/supabase/queries';
import type { InquiryFormData } from '../types';

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

const arrOptions = [
  { value: 'under_1m', label: 'Under $1M' },
  { value: '1m_5m', label: '$1M – $5M' },
  { value: '5m_20m', label: '$5M – $20M' },
  { value: '20m_plus', label: '$20M+' },
];

export function ContactPage() {
  const [formData, setFormData] = useState<InquiryFormData>({
    name: '',
    email: '',
    message: '',
  });
  const [whatTried, setWhatTried] = useState('');
  const [approximateArr, setApproximateArr] = useState('');
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Please tell us about your needs';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      let fullMessage = formData.message;
      if (whatTried) {
        fullMessage += `\n\n---\nWhat they've already tried:\n${whatTried}`;
      }
      if (approximateArr) {
        const arrLabel = arrOptions.find(opt => opt.value === approximateArr)?.label || approximateArr;
        fullMessage += `\n\n---\nApproximate ARR: ${arrLabel}`;
      }

      const result = await createInquiry({
        ...formData,
        message: fullMessage,
      });

      if (result) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          message: '',
        });
        setWhatTried('');
        setApproximateArr('');
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-obsidian">
      {/* Hero */}
      <section className="section-padding bg-slate-50 dark:bg-obsidian">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-heading font-black text-obsidian dark:text-signal">
              Book a diagnostic
            </h1>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              Tell us what's happening. We'll tell you if we can help and how.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section-padding bg-white dark:bg-obsidian-50 border-t border-slate-200 dark:border-slate-800">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Form */}
            <div>
              <h2 className="text-2xl font-heading font-black text-obsidian dark:text-signal mb-6">
                Send us a message
              </h2>

              {submitStatus === 'success' ? (
                <Card variant="bordered" padding="lg" className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800">
                  <CardContent>
                    <div className="flex items-start gap-4">
                      <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <h3 className="font-semibold text-green-800 dark:text-green-200">
                          Message sent successfully
                        </h3>
                        <p className="mt-2 text-green-700 dark:text-green-300">
                          We'll review your inquiry and get back to you within 1-2 business days.
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-4"
                          onClick={() => setSubmitStatus('idle')}
                        >
                          Send another message
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  {submitStatus === 'error' && (
                    <div className="p-4 rounded bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
                      <div className="flex items-center gap-2">
                        <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                        <p className="text-red-600 dark:text-red-300 text-sm">
                          Something went wrong. Please try again or email us directly.
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      label="Your name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      error={errors.name}
                      placeholder="John Smith"
                      required
                    />
                    <Input
                      label="Work email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={errors.email}
                      placeholder="john@company.com"
                      required
                    />
                  </div>

                  <Textarea
                    label="What's the churn problem?"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    error={errors.message}
                    placeholder="Tell us about your retention challenges. What's happening? How long has it been a problem? What are the stakes?"
                    rows={4}
                    required
                  />

                  <Textarea
                    label="What have you already tried?"
                    name="whatTried"
                    value={whatTried}
                    onChange={(e) => setWhatTried(e.target.value)}
                    placeholder="Health scores, QBRs, CSM reorganization, new tools... what hasn't worked?"
                    rows={3}
                  />

                  <Select
                    label="Approximate ARR"
                    name="approximateArr"
                    value={approximateArr}
                    onChange={(e) => setApproximateArr(e.target.value)}
                    options={arrOptions}
                    placeholder="Select range"
                  />

                  <Button
                    type="submit"
                    size="lg"
                    isLoading={isSubmitting}
                    className="w-full sm:w-auto"
                  >
                    Send message
                  </Button>
                </form>
              )}
            </div>

            {/* Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-heading font-black text-obsidian dark:text-signal mb-6">
                  Other ways to connect
                </h2>

                <div className="space-y-4">
                  <Card variant="bordered" padding="md">
                    <CardContent>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded bg-amber/10 border border-amber/20 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-amber" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-obsidian dark:text-signal">
                            Email us
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                            For general inquiries or questions
                          </p>
                          <a
                            href="mailto:hello@churnlogix.com"
                            className="text-amber text-sm font-medium hover:underline mt-2 inline-block"
                          >
                            hello@churnlogix.com
                          </a>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card variant="bordered" padding="md">
                    <CardContent>
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded bg-amber/10 border border-amber/20 flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="w-5 h-5 text-amber" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-obsidian dark:text-signal">
                            Quick response
                          </h3>
                          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">
                            We typically respond within 24 hours on business days.
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              <Card variant="bordered" padding="lg" className="bg-amber/5 border-amber/20">
                <CardContent>
                  <h3 className="font-heading font-black text-obsidian dark:text-signal mb-4">
                    What to expect
                  </h3>
                  <div className="space-y-3">
                    {[
                      'No high-pressure sales tactics',
                      'Honest assessment of whether we can help',
                      'Actionable insights even if we don\'t work together',
                      'Full confidentiality guaranteed',
                    ].map((item) => (
                      <div key={item} className="flex items-start gap-2">
                        <CheckCircle className="w-5 h-5 text-amber flex-shrink-0 mt-0.5" />
                        <span className="text-slate-600 dark:text-slate-400 text-sm">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ContactPage;
